import type { ViewSetting } from '@/domains/access/access.types';

/**
 * A view is identified by its name. The set of views, and the role each one
 * requires, is data-driven: it comes from the backend (`GET /system/views`,
 * persisted in Cosmos `orr-config`) — see {@link useEvaluationViews}. Nothing
 * here hardcodes the view list or its role gating anymore.
 */
export type EvaluationView = string;

export const DEFAULT_VIEW = 'default';

// Display labels for the well-known views; any other view (e.g. a newly created
// one) falls back to its upper-cased name.
const KNOWN_VIEW_LABELS: Record<string, string> = {
  default: 'DEFAULT',
  dr: 'DR',
  network: 'NETWORK',
  nova: 'NOVA',
};

export const viewLabel = (view: EvaluationView): string =>
  KNOWN_VIEW_LABELS[view] ?? view.toUpperCase();

/**
 * Mirrors the backend `ViewAuthorizationAttribute`:
 *  - the `default` view is always accessible;
 *  - `requiredAccessRole` null/empty => visible to everyone;
 *  - `"*"` => app-only callers (never an interactive user);
 *  - otherwise the user must hold the role.
 */
export const canAccessEvaluationView = (
  view: ViewSetting,
  roles: readonly string[] | undefined,
): boolean => {
  if (view.name === DEFAULT_VIEW) return true;
  const required = view.requiredAccessRole;
  if (!required) return true;
  if (required === '*') return false;
  return roles?.includes(required) ?? false;
};

export const getAvailableEvaluationViews = (
  views: readonly ViewSetting[],
  roles?: readonly string[],
): EvaluationView[] => {
  const accessible = views
    .filter((view) => canAccessEvaluationView(view, roles))
    .map((view) => view.name);
  // `default` is always available, even before /system/views has loaded.
  return accessible.includes(DEFAULT_VIEW)
    ? accessible
    : [DEFAULT_VIEW, ...accessible];
};

export const getAccessibleEvaluationView = (
  requestedView: string | null | undefined,
  views: readonly ViewSetting[],
  roles?: readonly string[],
): EvaluationView => {
  if (!requestedView || requestedView === DEFAULT_VIEW) return DEFAULT_VIEW;
  // Optimistic while /system/views is still loading (empty list): trust the URL
  // so a deep link such as `?view=nova` is not collapsed to default mid-load.
  if (views.length === 0) return requestedView;
  const match = views.find((view) => view.name === requestedView);
  return match && canAccessEvaluationView(match, roles)
    ? requestedView
    : DEFAULT_VIEW;
};
